import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "securitySelectFirst", "dateAsAtFirst", "yieldFirst", "marginFirst", "capitalPriceFirst", "grossFirst", "calculateButtonFirst", "updateChartButtonFirst", "volumeFirst",
    "toggleSecondCalculatorButton", "calculatorsContainer", "firstCalculator", "secondCalculator", "cashFlowChart", "cashFlowTable", "rightColumn",
    "securitySelectSecond", "dateAsAtSecond", "yieldSecond", "marginSecond", "capitalPriceSecond", "grossSecond", "calculateButtonSecond", "volumeSecond",
    "firstCalculatorRiskMarket", "updateChartButtonSecond", "comparisonTable", "accruedFirst", "accruedSecond",
    "validationMessageFirst", "validationMessageSecond"
  ]

  isUpdatingFields = false
  isUpdatingFieldsSecond = false
  hasUserInput = false
  userChangedField = null

  // Volume storage and debouncing
  storedVolumeFirst = '20000' // Default value from HTML
  storedVolumeSecond = '20000'
  volumeTimeoutFirst = null
  volumeTimeoutSecond = null

  connect() {
    window.calculatorController = this

    this.setupMarketValueListeners()
    this.enableUpdateChartButton()
    this.preventFormSubmission()// Update calculate button state
    this.updateCalculateButtonState()

    this.initializeStoredValues()

    // Listen for custom event from Turbo Stream responses
    document.addEventListener('calculationResultsLoaded', () => {
      this.onCalculationResultsLoaded()
    })

    setTimeout(() => {
      this.checkComparisonTableVisibility()
    }, 200)

    this.setupTurboStreamListener()

    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 100)

    // Initialize cash flow sections visibility
    this.updateCashFlowSectionsVisibility()
  }

  toggleSecondCalculator() {
    const secondCalculator = this.secondCalculatorTarget
    const toggleButton = this.toggleSecondCalculatorButtonTarget
    const cashFlowChart = this.cashFlowChartTarget
    const cashFlowTable = this.cashFlowTableTarget
    const rightColumn = this.rightColumnTarget
    const firstCalculatorRiskMarket = this.firstCalculatorRiskMarketTarget

    const mobileLabel = toggleButton.querySelector("[data-calculator-toggle-mobile-label]")
    const desktopLabel = toggleButton.querySelector("[data-calculator-toggle-desktop-label]")

    if (secondCalculator.classList.contains('hidden')) {
      this.storeRightColumnValues()

      // Show second calculator
      secondCalculator.classList.remove('hidden')
      if (mobileLabel) mobileLabel.textContent = "\u2212"
      if (desktopLabel) desktopLabel.textContent = "Remove Second Calculator"
      toggleButton.classList.remove('bg-neutral-100', 'text-neutral-700', 'border-neutral-300')
      toggleButton.classList.add('bg-primary', 'text-white', 'border-primary', 'hover:bg-white', 'hover:text-primary')

      rightColumn.classList.add('hidden')
      firstCalculatorRiskMarket.classList.remove('hidden')

      this.restoreValuesToFirstCalculator()

      // Sync volume fields and recalculate Market Value fields for the hidden section (which becomes visible)
      this.syncVolumeFields()
      setTimeout(() => {
        this.recalculateMarketValues('first')
      }, 200)

      this.enableUpdateChartButton()

      setTimeout(() => {
        this.checkComparisonTableVisibility()
        this.updateComparisonTable()
      }, 100)
    } else {
      this.storeFirstCalculatorValues()

      secondCalculator.classList.add('hidden')
      if (mobileLabel) mobileLabel.textContent = "+"
      if (desktopLabel) desktopLabel.textContent = "Add Second Calculator"
      toggleButton.classList.remove('bg-primary', 'text-white', 'border-primary', 'hover:bg-white', 'hover:text-primary')
      toggleButton.classList.add('bg-primary', 'text-white', 'border-primary', 'hover:bg-white', 'hover:text-primary')

      rightColumn.classList.remove('hidden')

      this.restoreValuesToRightColumn()

      // Sync volume fields and recalculate Market Value fields for the visible section (which becomes active again)
      this.syncVolumeFields()
      setTimeout(() => {
        this.recalculateMarketValues('first')
      }, 200)

      firstCalculatorRiskMarket.classList.add('hidden')

      this.enableUpdateChartButton()
      this.comparisonTableTarget.classList.add('hidden')

      // Auto-generate chart when switching back to single calculator
      setTimeout(() => {
        this.generateChartData()
      }, 400)
    }

    // Update cash flow sections visibility
    this.updateCashFlowSectionsVisibility()
  }

  updateCashFlowSectionsVisibility() {
    const secondCalculator = this.secondCalculatorTarget
    const cashFlowChart = this.cashFlowChartTarget
    const cashFlowTable = this.cashFlowTableTarget
    const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

    if (isSecondCalculatorActive) {
      // Hide cash flow sections when both calculators are present
      cashFlowChart.classList.add('hidden')
      cashFlowTable.classList.add('hidden')
    } else {
      // Show cash flow sections when only one calculator is present
      cashFlowChart.classList.remove('hidden')
      cashFlowTable.classList.remove('hidden')
    }
  }

  selectSecurityFirst(event) {
    this.selectSecurity(event, 'first')
  }

  selectSecuritySecond(event) {
    this.selectSecurity(event, 'second')
  }

  selectSecurity(event, calculatorType) {
    const securityId = event.target.value
    const form = event.target.closest('form')

    if (form) {
      const hiddenField = form.querySelector('input[name="security_id"]')
      if (hiddenField) {
        hiddenField.value = securityId || ''
      }

      // Update the security_id field in the corresponding calculation form
      const calculationForms = document.querySelectorAll('form[action*="calculate_security"]')
      const targetCalculationForm = Array.from(calculationForms).find(form =>
        form.querySelector(`input[name="calculation"][value="${calculatorType}"]`)
      )
      if (targetCalculationForm) {
        const calculationSecurityIdField = targetCalculationForm.querySelector('input[name="security_id"]')
        if (calculationSecurityIdField) {
          calculationSecurityIdField.value = securityId || ''
        }
      }

      this.updateCalculateButtonState()

      setTimeout(() => {
        this.checkComparisonTableVisibility()
      }, 100)

      if (calculatorType === 'first') {
        setTimeout(() => {
          this.updateFirstCalculatorRiskMarket()
        }, 200)
      }

      form.requestSubmit()

      setTimeout(() => {
        this.updateCalculateButtonState()
        // Recalculate Market Value fields after security selection
        this.recalculateMarketValues(calculatorType)
      }, 300)
    }
  }

  enableCalculate() {
    if (this.hasCalculateButtonFirstTarget) {
      this.calculateButtonFirstTarget.disabled = false
      this.calculateButtonFirstTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  clearOtherFields(changedField) {
    if (this.isUpdatingFields) {
      return
    }

    // Clear all other calculation fields when user enters a new value
    // Keep the changed field, date_as_at, and accrued fields
    const fields = ['yieldFirst', 'marginFirst', 'capitalPriceFirst', 'grossFirst']
    fields.forEach(field => {
      if (field !== changedField) {
        const target = this[field + 'Target']
        if (target) {
          // Clear the field regardless of its current value
          target.value = ''
        }
      }
    })
  }

  yieldFirstChanged() {
    this.enableCalculate()
    this.hasUserInput = true
    this.userChangedField = 'yieldFirst'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFields) {
      this.clearOtherFields('yieldFirst')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 50)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('first')
    }, 100)
  }

  marginFirstChanged() {
    this.enableCalculate()
    this.hasUserInput = true
    this.userChangedField = 'marginFirst'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFields) {
      this.clearOtherFields('marginFirst')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 50)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('first')
    }, 100)
  }

  capitalPriceFirstChanged() {
    this.enableCalculate()
    this.hasUserInput = true
    this.userChangedField = 'capitalPriceFirst'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFields) {
      this.clearOtherFields('capitalPriceFirst')
    }

    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 50)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('first')
    }, 100)
  }

  grossFirstChanged() {
    this.enableCalculate()
    this.hasUserInput = true
    this.userChangedField = 'grossFirst'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFields) {
      this.clearOtherFields('grossFirst')
    }

    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 50)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('first')
    }, 100)
  }

  yieldSecondChanged() {
    this.enableCalculateSecond()
    this.hasUserInput = true
    this.userChangedField = 'yieldSecond'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFieldsSecond) {
      this.clearOtherFieldsSecond('yieldSecond')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('second')
    }, 100)
  }

  marginSecondChanged() {
    this.enableCalculateSecond()
    this.hasUserInput = true
    this.userChangedField = 'marginSecond'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFieldsSecond) {
      this.clearOtherFieldsSecond('marginSecond')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('second')
    }, 100)
  }

  capitalPriceSecondChanged() {
    this.enableCalculateSecond()
    this.hasUserInput = true
    this.userChangedField = 'capitalPriceSecond'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFieldsSecond) {
      this.clearOtherFieldsSecond('capitalPriceSecond')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('second')
    }, 100)
  }

  grossSecondChanged() {
    this.enableCalculateSecond()
    this.hasUserInput = true
    this.userChangedField = 'grossSecond'
    // Clear other fields when user enters a new value
    if (!this.isUpdatingFieldsSecond) {
      this.clearOtherFieldsSecond('grossSecond')
    }
    setTimeout(() => {
      this.updateComparisonTable()
    }, 100)

    // Recalculate Market Value fields
    setTimeout(() => {
      this.recalculateMarketValues('second')
    }, 100)
  }

  enableCalculateSecond() {
    if (this.hasCalculateButtonSecondTarget) {
      this.calculateButtonSecondTarget.disabled = false
      this.calculateButtonSecondTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  clearOtherFieldsSecond(changedField) {
    if (this.isUpdatingFieldsSecond) {
      return
    }

    // Clear all other calculation fields when user enters a new value
    // Keep the changed field, date_as_at, and accrued fields
    const fields = ['yieldSecond', 'marginSecond', 'capitalPriceSecond', 'grossSecond']
    fields.forEach(field => {
      if (field !== changedField) {
        // Access the target directly using the correct property name
        const target = this[field + 'Target']
        if (target) {
          // Clear the field regardless of its current value
          target.value = ''
        }
      }
    })
  }

  dateAsAtFirstChanged() {
    this.enableCalculate()
    setTimeout(() => {
      this.updateCalculateButtonState()
    }, 50)
  }

  dateAsAtSecondChanged() {
    this.enableCalculateSecond()
  }

  updateChartFirst() {
    // Generate chart data based on current security and market values for first calculator
    this.generateChartData()
  }

  updateChartSecond() {
    // Generate chart data based on current security and market values for second calculator
    this.generateChartData()
  }

  updateChart() {
    // Generate chart data based on current security and market values
    this.generateChartData()
  }

  generateChartData() {
    // Check if second calculator is active - if so, don't generate chart data
    const secondCalculator = this.secondCalculatorTarget
    const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

    if (isSecondCalculatorActive) {
      return
    }

    const securitySelect = document.querySelector('input[data-calculator-target="securitySelectFirst"]')
    const securityId = securitySelect ? securitySelect.value : ''

    if (securityId && securityId.trim() !== '') {
      const secondCalculator = this.secondCalculatorTarget
      const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

      let volume, currency, marketCapitalValue, marketAccruedInterest, marketValue

      // Use stored volume value instead of reading from DOM
      volume = this.storedVolumeFirst || ''

      if (isSecondCalculatorActive) {
        currency = document.querySelector('input[name="currency_first"]')?.value || ''
        marketCapitalValue = document.querySelector('input[name="market_capital_value_first_hidden"]')?.value || ''
        marketAccruedInterest = document.querySelector('input[name="market_accrued_interest_first_hidden"]')?.value || ''
        marketValue = document.querySelector('input[name="market_value_first_hidden"]')?.value || ''
      } else {
        currency = document.querySelector('input[name="currency_first"]')?.value || ''
        marketCapitalValue = document.querySelector('input[name="market_capital_value_first"]')?.value || ''
        marketAccruedInterest = document.querySelector('input[name="market_accrued_interest_first"]')?.value || ''
        marketValue = document.querySelector('input[name="market_value_first"]')?.value || ''
      }

      const formData = new FormData()
      formData.append('security_id', securityId)
      formData.append('volume', volume)
      formData.append('currency', currency)
      formData.append('market_capital_value', marketCapitalValue)
      formData.append('market_accrued_interest', marketAccruedInterest)
      formData.append('market_value', marketValue)

      fetch('/tools/update_chart', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })
      .then(response => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error('Network response was not ok')
        }
      })
      .then(html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const turboStreams = doc.querySelectorAll('turbo-stream')

        turboStreams.forEach((turboStream, index) => {
          const targetId = turboStream.getAttribute('target')
          const target = document.getElementById(targetId)

          if (target) {
            const template = turboStream.querySelector('template')
            if (template) {
              target.innerHTML = template.innerHTML
            } else {
              target.innerHTML = turboStream.innerHTML
            }

            const scripts = turboStream.querySelectorAll('script')

            scripts.forEach((script, index) => {
              try {
                let scriptContent = script.textContent

                scriptContent = scriptContent.replace(
                  /window\.addEventListener\('load',\s*function\(\)\s*\{([\s\S]*?)\}\);/g,
                  '(function() { $1 })();'
                )

                eval(scriptContent)
              } catch (e) {
                console.error(`Error executing script ${index}:`, e)
              }
            })

            setTimeout(() => {
              if (typeof Highcharts !== 'undefined') {
                const chartScripts = target.querySelectorAll('script[type="text/javascript"]')
                chartScripts.forEach((script, index) => {
                  if (script.textContent.includes('Highcharts.Chart')) {
                    try {
                      let scriptContent = script.textContent
                      scriptContent = scriptContent.replace(
                        /window\.addEventListener\('load',\s*function\(\)\s*\{([\s\S]*?)\}\);/g,
                        '(function() { $1 })();'
                      )
                      eval(scriptContent)
                    } catch (e) {
                      console.error(`Error executing Highcharts script ${index}:`, e)
                    }
                  }
                })
                Highcharts.charts.forEach(chart => {
                  if (chart && chart.renderTo && chart.renderTo.id === 'cashflow-graph') {
                    chart.reflow()
                  }
                })
              }
            }, 100)

            if (typeof Highcharts !== 'undefined') {
              setTimeout(() => {
                if (typeof window.dispatchEvent !== 'undefined') {
                  window.dispatchEvent(new Event('resize'))
                }
              }, 100)
            }

            // Ensure chart and table containers are visible only when second calculator is not active
            const secondCalculator = this.secondCalculatorTarget
            const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

            if (!isSecondCalculatorActive) {
              const chartContainer = document.getElementById('cash-flow-chart')
              const tableContainer = document.getElementById('cash-flow-table')

              if (chartContainer) {
                chartContainer.classList.remove('hidden')
              }

              if (tableContainer) {
                tableContainer.classList.remove('hidden')
                const tableBody = tableContainer.querySelector('#cash-flow-tbody')
                if (tableBody) {
                } else {
                }
              }
            }
          }
        })
      })
      .catch(error => {
        console.error('Error loading chart data:', error)
        const chartContainer = document.getElementById('cashflow-graph')
        if (chartContainer) {
          chartContainer.innerHTML = '<div class="flex items-center justify-center h-full text-red-500"><div class="text-center"><i class="fa fa-exclamation-triangle text-4xl mb-4"></i><p>Error loading chart data</p></div></div>'
        }
      })
    } else {
      const chartContainer = document.getElementById('cashflow-graph')
      if (chartContainer) {
        chartContainer.innerHTML = '<div class="flex items-center justify-center h-full text-neutral-500"><div class="text-center"><i class="fa fa-exclamation-triangle text-4xl mb-4"></i><p>Please select a security first</p></div></div>'
      }
    }
  }

  // Add event listeners for market value changes
  setupMarketValueListeners() {
    const marketValueFields = [
      'input[name="volume_first"]',
      'input[name="market_capital_value_first"]',
      'input[name="market_accrued_interest_first"]',
      'input[name="market_value_first"]',
      'input[name="volume_second"]',
      'input[name="market_capital_value_second"]',
      'input[name="market_accrued_interest_second"]',
      'input[name="market_value_second"]'
    ]
    marketValueFields.forEach(selector => {
      const field = document.querySelector(selector)
      if (field) {
        field.addEventListener('change', () => {
          if (selector === 'input[name="volume_first"]') {
            this.enableUpdateChartButton()
          }
          // Update chart when market values change (only for single calculator)
          if (selector.includes('volume') || selector.includes('market_')) {
            this.generateChartData()
          }
          // Update comparison table when market values change
          setTimeout(() => {
            this.updateComparisonTable()
          }, 100)
        })
      }
    })

    // Also set up listeners for currency fields
    const currencyFields = [
      'input[name="currency_first"]',
      'input[name="currency_second"]'
    ]

    currencyFields.forEach(selector => {
      const field = document.querySelector(selector)
      if (field) {
        field.addEventListener('change', () => {
          // Update chart when currency changes (only for single calculator)
          this.generateChartData()
          // Update comparison table when currency changes
          setTimeout(() => {
            this.updateComparisonTable()
          }, 100)
        })
      }
    })
  }

  enableUpdateChartButton() {
    const volumeFirstField = document.querySelector('input[name="volume_first"]')
    const updateChartButtonFirst = document.querySelector('[data-calculator-target="updateChartButtonFirst"]')

    // Check if second calculator is active
    const secondCalculator = this.secondCalculatorTarget
    const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

    // Update cash flow sections visibility
    this.updateCashFlowSectionsVisibility()

    if (isSecondCalculatorActive) {
      // Hide update chart button when second calculator is active
      if (updateChartButtonFirst) {
        updateChartButtonFirst.classList.add('hidden')
      }
    } else {
      // Show the update chart button when only one calculator is active
      if (updateChartButtonFirst) {
        updateChartButtonFirst.classList.remove('hidden')
        const volume = volumeFirstField?.value.trim()
        if (volume && parseFloat(volume) > 0) {
          updateChartButtonFirst.disabled = false
          updateChartButtonFirst.classList.remove('opacity-50', 'cursor-not-allowed')
          updateChartButtonFirst.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed')
          updateChartButtonFirst.classList.add('cursor-pointer')
        } else {
          updateChartButtonFirst.disabled = true
          updateChartButtonFirst.classList.add('opacity-50', 'cursor-not-allowed')
          updateChartButtonFirst.classList.add('disabled:opacity-50', 'disabled:cursor-not-allowed')
          updateChartButtonFirst.classList.remove('cursor-pointer')
        }
      }
    }

    // Also update the update chart button state after restoring values
    setTimeout(() => {
      if (isSecondCalculatorActive) {
        // Keep button hidden when second calculator is active
        if (updateChartButtonFirst) updateChartButtonFirst.classList.add('hidden')
      } else {
        // Show the update chart button when single calculator is active
        if (updateChartButtonFirst) {
          updateChartButtonFirst.classList.remove('hidden')
          const volume = volumeFirstField?.value.trim()
          if (volume && parseFloat(volume) > 0) {
            updateChartButtonFirst.disabled = false
            updateChartButtonFirst.classList.remove('opacity-50', 'cursor-not-allowed')
            updateChartButtonFirst.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed')
            updateChartButtonFirst.classList.add('cursor-pointer')
          } else {
            updateChartButtonFirst.disabled = true
            updateChartButtonFirst.classList.add('opacity-50', 'cursor-not-allowed')
            updateChartButtonFirst.classList.add('disabled:opacity-50', 'disabled:cursor-not-allowed')
            updateChartButtonFirst.classList.remove('cursor-pointer')
          }
        }
      }
    }, 100)
  }

  volumeFirstChanged() {
    // Clear existing timeout
    if (this.volumeTimeoutFirst) {
      clearTimeout(this.volumeTimeoutFirst)
    }

    // Find the correct volume field based on current state
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')
    let volumeField

    if (isSecondCalculatorActive) {
      // When second calculator is active, use the hidden volume field
      volumeField = document.querySelector('input[name="volume_first"]')
    } else {
      // When only first calculator is active, use the visible volume field
      volumeField = document.querySelector('[data-calculator-target="rightColumn"] input[name="volume_first"]')
    }

    // Store the volume value immediately
    this.storedVolumeFirst = volumeField ? volumeField.value : ''

    // Sync the volume field in the other section (visible <-> hidden)
    if (isSecondCalculatorActive) {
      // When second calculator is active, update the visible volume field
      const visibleVolumeField = document.querySelector('[data-calculator-target="rightColumn"] input[name="volume_first"]')
      if (visibleVolumeField) {
        visibleVolumeField.value = this.storedVolumeFirst
      }
    } else {
      // When only first calculator is active, update the hidden volume field
      const hiddenVolumeField = document.querySelector('input[name="volume_first"]')
      if (hiddenVolumeField) {
        hiddenVolumeField.value = this.storedVolumeFirst
      }
    }


    // Set up debounced recalculation (1 second delay)
    this.volumeTimeoutFirst = setTimeout(() => {
      this.recalculateMarketValues('first')

      // Auto-generate chart when volume changes (only for single calculator)
      if (!isSecondCalculatorActive) {
        this.generateChartData()
      }

      // Update comparison table when volume changes
      this.updateComparisonTable()
    }, 1000)

    this.enableUpdateChartButton()
  }

  volumeSecondChanged() {
    // Clear existing timeout
    if (this.volumeTimeoutSecond) {
      clearTimeout(this.volumeTimeoutSecond)
    }

    // Store the volume value immediately
    this.storedVolumeSecond = this.volumeSecondTarget.value

    // Set up debounced recalculation (1 second delay)
    this.volumeTimeoutSecond = setTimeout(() => {
      this.recalculateMarketValues('second')

      // Update comparison table when volume changes
      this.updateComparisonTable()
    }, 1000)

    this.enableUpdateChartButton()
  }

  setFieldsProgrammatically() {
    this.isUpdatingFields = true
    // Set a longer timeout to ensure all field updates complete
    setTimeout(() => {
      this.isUpdatingFields = false
      // Reset the flags after calculation is complete
      this.hasUserInput = false
      this.userChangedField = null
    }, 3000) // Increased timeout to 3 seconds to ensure field updates complete
  }

  setFieldsProgrammaticallySecond() {
    this.isUpdatingFieldsSecond = true
    // Set a longer timeout to ensure all field updates complete
    setTimeout(() => {
      this.isUpdatingFieldsSecond = false
      // Reset the flags after calculation is complete
      this.hasUserInput = false
      this.userChangedField = null
    }, 3000) // Increased timeout to 3 seconds to ensure field updates complete
  }

  // Method to prevent form submission during field updates
  preventFormSubmission() {
    const forms = document.querySelectorAll('form[action*="calculate_security"]')
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Only prevent submission if we're actively updating fields AND this is not a manual button click
        if (this.isUpdatingFields && !e.submitter) {
          e.preventDefault()
          e.stopPropagation()
          return false
        }
      })
    })
  }

  // Method to submit form after field updates are complete
  submitFormAfterUpdate(form) {
    setTimeout(() => {
      if (!this.isUpdatingFields) {
        form.submit()
      } else {
        this.submitFormAfterUpdate(form)
      }
    }, 100)
  }

  // Method to handle calculate button clicks for first calculator
  calculateButtonFirstClicked(event) {
    this.calculateButtonClicked(event, 'first')
  }

  // Method to handle calculate button clicks for second calculator
  calculateButtonSecondClicked(event) {
    this.calculateButtonClicked(event, 'second')
  }

  // Method to handle calculate button clicks
  calculateButtonClicked(event, calculatorType) {
    event.preventDefault() // Prevent default form submission

    const form = event.target.closest('form')
    if (form) {
      let securitySelect
      if (calculatorType === 'second') {
        securitySelect = document.querySelector('input[data-calculator-target="securitySelectSecond"]')
      } else {
        securitySelect = document.querySelector('input[data-calculator-target="securitySelectFirst"]')
      }

      if (securitySelect && securitySelect.value) {
        const selectedSecurityId = securitySelect.value
        const securityIdField = form.querySelector('input[name="security_id"]')
        if (securityIdField) {
          securityIdField.value = selectedSecurityId
        } else {
          console.error('security_id field not found in calculation form')
          return
        }
      } else {
        console.error('No security selected for this calculator')
        alert('Please select a security before calculating')
        return
      }

      const validationError = this.validateCalculatorSubmission(calculatorType)
      if (validationError) {
        this.showValidationMessage(calculatorType, validationError)
        return
      }
      this.hideValidationMessage(calculatorType)

      // Set only the user's input fields
      this.setUserInputFields(form)

      form.requestSubmit()

      setTimeout(() => {
        this.onCalculationResultsLoaded()
      }, 500)
    }
  }

  validateCalculatorSubmission(calculatorType) {
    const isSecond = calculatorType === 'second'
    const suffix = isSecond ? 'Second' : 'First'
    const readTarget = (base) => {
      const el = document.querySelector(`[data-calculator-target="${base}${suffix}"]`)
      return el ? String(el.value ?? '').trim() : ''
    }

    const dateVal = readTarget('dateAsAt')
    if (!dateVal) return 'Choose a value date.'

    const yieldVal = readTarget('yield')
    const marginVal = readTarget('margin')
    const capVal = readTarget('capitalPrice')
    const grossVal = readTarget('gross')

    const fields = [
      { key: 'yield', v: yieldVal },
      { key: 'margin', v: marginVal },
      { key: 'capital_price', v: capVal },
      { key: 'gross', v: grossVal }
    ]
    const filled = fields.filter((f) => f.v !== '')
    if (filled.length === 0) {
      return 'Enter at least one of yield to maturity, credit spread, capital price, or gross price.'
    }

    return null
  }

  showValidationMessage(calculatorType, message) {
    if (calculatorType === 'second') {
      if (this.hasValidationMessageSecondTarget) {
        this.validationMessageSecondTarget.textContent = message
        this.validationMessageSecondTarget.classList.remove('hidden')
      }
    } else if (this.hasValidationMessageFirstTarget) {
      this.validationMessageFirstTarget.textContent = message
      this.validationMessageFirstTarget.classList.remove('hidden')
    }
  }

  hideValidationMessage(calculatorType) {
    if (calculatorType === 'second') {
      if (this.hasValidationMessageSecondTarget) {
        this.validationMessageSecondTarget.textContent = ''
        this.validationMessageSecondTarget.classList.add('hidden')
      }
    } else if (this.hasValidationMessageFirstTarget) {
      this.validationMessageFirstTarget.textContent = ''
      this.validationMessageFirstTarget.classList.add('hidden')
    }
  }

  hideAllValidationMessages() {
    this.hideValidationMessage('first')
    this.hideValidationMessage('second')
  }

  // Method to check if a security is selected and update calculate button state
  updateCalculateButtonState() {
    const securitySelectFirst = document.querySelector('input[data-calculator-target="securitySelectFirst"]')
    const securitySelectSecond = document.querySelector('input[data-calculator-target="securitySelectSecond"]')

    const hasFirstSecurity = securitySelectFirst && securitySelectFirst.value && securitySelectFirst.value.trim() !== ''
    const hasSecondSecurity = securitySelectSecond && securitySelectSecond.value && securitySelectSecond.value.trim() !== ''

    if (this.hasCalculateButtonFirstTarget) {
      if (hasFirstSecurity) {
        this.calculateButtonFirstTarget.disabled = false
        this.calculateButtonFirstTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      } else {
        this.calculateButtonFirstTarget.disabled = true
        this.calculateButtonFirstTarget.classList.add('opacity-50', 'cursor-not-allowed')
      }
    }

    // Update second calculator button
    if (this.hasCalculateButtonSecondTarget) {
      if (hasSecondSecurity) {
        this.calculateButtonSecondTarget.disabled = false
        this.calculateButtonSecondTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      } else {
        this.calculateButtonSecondTarget.disabled = true
        this.calculateButtonSecondTarget.classList.add('opacity-50', 'cursor-not-allowed')
      }
    }
    this.enableUpdateChartButton()
  }

  // Store values from right column (Risk and Market Value sections)
  storeRightColumnValues() {
    // Use visible section selectors when storing from right column
    const visibleSection = document.querySelector('[data-calculator-target="rightColumn"]')
    this.storedRightColumnValues = {
      volume: visibleSection?.querySelector('input[name="volume_first"]')?.value || this.storedVolumeFirst || '',
      currency: visibleSection?.querySelector('input[name="currency_first"]')?.value || '',
      marketCapitalValue: document.querySelector('input[name="market_capital_value_first"]')?.value || '',
      marketAccruedInterest: document.querySelector('input[name="market_accrued_interest_first"]')?.value || '',
      marketValue: document.querySelector('input[name="market_value_first"]')?.value || '',
      termToMaturity: document.getElementById('term-to-maturity-first')?.textContent || '',
      modifiedDuration: document.getElementById('modified-duration-first')?.textContent || '',
      creditDuration: document.getElementById('credit-duration-first')?.textContent || ''
    }
  }

  // Store values from first calculator's Risk and Market Value sections
  storeFirstCalculatorValues() {
    this.storedFirstCalculatorValues = {
      volume: this.storedVolumeFirst || '',
      currency: document.querySelector('input[name="currency_first"]')?.value || '',
      marketCapitalValue: document.querySelector('input[name="market_capital_value_first"]')?.value || '',
      marketAccruedInterest: document.querySelector('input[name="market_accrued_interest_first"]')?.value || '',
      marketValue: document.querySelector('input[name="market_value_first"]')?.value || '',
      termToMaturity: document.getElementById('term-to-maturity-first')?.textContent || '',
      modifiedDuration: document.getElementById('modified-duration-first')?.textContent || '',
      creditDuration: document.getElementById('credit-duration-first')?.textContent || ''
    }
  }

  // Restore values to first calculator's Risk and Market Value sections
  restoreValuesToFirstCalculator() {
    if (this.storedRightColumnValues) {
      const values = this.storedRightColumnValues

      // Restore market value fields
      const volumeField = document.querySelector('input[name="volume_first"]')
      const currencyField = document.querySelector('input[name="currency_first"]')
      const marketCapitalValueField = document.querySelector('input[name="market_capital_value_first"]')
      const marketAccruedInterestField = document.querySelector('input[name="market_accrued_interest_first"]')
      const marketValueField = document.querySelector('input[name="market_value_first"]')

      if (volumeField) volumeField.value = values.volume
      if (currencyField) currencyField.value = values.currency
      if (marketCapitalValueField) marketCapitalValueField.value = values.marketCapitalValue
      if (marketAccruedInterestField) marketAccruedInterestField.value = values.marketAccruedInterest
      if (marketValueField) marketValueField.value = values.marketValue

      // Restore risk values
      const termToMaturityElement = document.getElementById('term-to-maturity-first')
      const modifiedDurationElement = document.getElementById('modified-duration-first')
      const creditDurationElement = document.getElementById('credit-duration-first')

      if (termToMaturityElement) termToMaturityElement.textContent = values.termToMaturity
      if (modifiedDurationElement) modifiedDurationElement.textContent = values.modifiedDuration
      if (creditDurationElement) creditDurationElement.textContent = values.creditDuration
    }
  }

  // Restore values to right column (Risk and Market Value sections)
  restoreValuesToRightColumn() {
    if (this.storedFirstCalculatorValues) {
      const values = this.storedFirstCalculatorValues

      // Restore market value fields
      const volumeField = document.querySelector('input[name="volume_first"]')
      const currencyField = document.querySelector('input[name="currency_first"]')
      const marketCapitalValueField = document.querySelector('input[name="market_capital_value_first"]')
      const marketAccruedInterestField = document.querySelector('input[name="market_accrued_interest_first"]')
      const marketValueField = document.querySelector('input[name="market_value_first"]')

      if (volumeField) volumeField.value = values.volume
      if (currencyField) currencyField.value = values.currency
      if (marketCapitalValueField) marketCapitalValueField.value = values.marketCapitalValue
      if (marketAccruedInterestField) marketAccruedInterestField.value = values.marketAccruedInterest
      if (marketValueField) marketValueField.value = values.marketValue

      // Restore risk values
      const termToMaturityElement = document.getElementById('term-to-maturity-first')
      const modifiedDurationElement = document.getElementById('modified-duration-first')
      const creditDurationElement = document.getElementById('credit-duration-first')

      if (termToMaturityElement) termToMaturityElement.textContent = values.termToMaturity
      if (modifiedDurationElement) modifiedDurationElement.textContent = values.modifiedDuration
      if (creditDurationElement) creditDurationElement.textContent = values.creditDuration
    }
  }

  // Initialize stored values from current state
  initializeStoredValues() {
    // Check if second calculator is active
    const secondCalculator = this.secondCalculatorTarget
    const isSecondCalculatorActive = !secondCalculator.classList.contains('hidden')

    if (isSecondCalculatorActive) {
      // Store current values from first calculator's risk/market sections
      this.storeFirstCalculatorValues()
    } else {
      // Store current values from right column
      this.storeRightColumnValues()
    }
  }

  // Check if both calculators have securities selected and show/hide comparison table
  checkComparisonTableVisibility() {
    const securitySelectFirst = document.querySelector('input[data-calculator-target="securitySelectFirst"]')
    const securitySelectSecond = document.querySelector('input[data-calculator-target="securitySelectSecond"]')
    const comparisonTable = this.comparisonTableTarget

    const hasFirstSecurity = securitySelectFirst && securitySelectFirst.value && securitySelectFirst.value.trim() !== ''
    const hasSecondSecurity = securitySelectSecond && securitySelectSecond.value && securitySelectSecond.value.trim() !== ''
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')

    if (isSecondCalculatorActive && hasFirstSecurity && hasSecondSecurity) {
      comparisonTable.classList.remove('hidden')
      this.updateComparisonTable()
    } else {
      comparisonTable.classList.add('hidden')
    }

    // Update cash flow sections visibility
    this.updateCashFlowSectionsVisibility()
  }

  // Update first calculator's Risk and Market Value sections when security changes
  updateFirstCalculatorRiskMarket() {
    // Check if second calculator is active
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')

    if (isSecondCalculatorActive) {
      // Only update risk values, not Market Value fields (they should be calculated separately)
      const riskValues = {
        termToMaturity: document.getElementById('term-to-maturity-first')?.textContent || '',
        modifiedDuration: document.getElementById('modified-duration-first')?.textContent || '',
        creditDuration: document.getElementById('credit-duration-first')?.textContent || ''
      }

      // Update only risk values
      const termToMaturityElement = document.getElementById('term-to-maturity-first')
      const modifiedDurationElement = document.getElementById('modified-duration-first')
      const creditDurationElement = document.getElementById('credit-duration-first')

      if (termToMaturityElement) termToMaturityElement.textContent = riskValues.termToMaturity
      if (modifiedDurationElement) modifiedDurationElement.textContent = riskValues.modifiedDuration
      if (creditDurationElement) creditDurationElement.textContent = riskValues.creditDuration
    } else {
      // If only first calculator is active, recalculate Market Value fields
      setTimeout(() => {
        this.recalculateMarketValues('first')
      }, 100)
    }
  }


  // Update first calculator fields with provided values
  updateFirstCalculatorFields(values) {
    // Check if second calculator is active to determine which field names to use
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')
    const fieldSuffix = isSecondCalculatorActive ? '_first_hidden' : '_first'

    // Update market value fields
    const volumeField = document.querySelector('input[name="volume_first"]')
    const currencyField = document.querySelector('input[name="currency_first"]')
    const marketCapitalValueField = document.querySelector(`input[name="market_capital_value${fieldSuffix}"]`)
    const marketAccruedInterestField = document.querySelector(`input[name="market_accrued_interest${fieldSuffix}"]`)
    const marketValueField = document.querySelector(`input[name="market_value${fieldSuffix}"]`)

    if (volumeField) volumeField.value = values.volume
    if (currencyField) currencyField.value = values.currency
    if (marketCapitalValueField) marketCapitalValueField.value = values.marketCapitalValue
    if (marketAccruedInterestField) marketAccruedInterestField.value = values.marketAccruedInterest
    if (marketValueField) marketValueField.value = values.marketValue

    // Update risk values
    const termToMaturityElement = document.getElementById('term-to-maturity-first')
    const modifiedDurationElement = document.getElementById('modified-duration-first')
    const creditDurationElement = document.getElementById('credit-duration-first')

    if (termToMaturityElement) termToMaturityElement.textContent = values.termToMaturity
    if (modifiedDurationElement) modifiedDurationElement.textContent = values.modifiedDuration
    if (creditDurationElement) creditDurationElement.textContent = values.creditDuration

    // Update the stored values for future toggles
    this.storedRightColumnValues = values
  }

  // Update the comparison table with calculated differences
  updateComparisonTable() {
    // Check if second calculator is active to determine which fields to read from
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')

    // Get values from first calculator - use appropriate fields based on calculator state
    const firstYield = parseFloat(this.yieldFirstTarget.value) || 0
    const firstMargin = parseFloat(this.marginFirstTarget.value) || 0
    const firstVolume = this.parseFormattedNumber(document.querySelector('input[name="volume_first"]')?.value)

    let firstMarketValue
    if (isSecondCalculatorActive) {
      // When second calculator is active, read from hidden section
      firstMarketValue = this.parseFormattedNumber(document.querySelector('input[name="market_value_first_hidden"]')?.value)
    } else {
      // When only first calculator is active, read from visible section
      firstMarketValue = this.parseFormattedNumber(document.querySelector('input[name="market_value_first"]')?.value)
    }

    // Get values from second calculator
    const secondYield = parseFloat(this.yieldSecondTarget.value) || 0
    const secondMargin = parseFloat(this.marginSecondTarget.value) || 0
    const secondVolume = this.parseFormattedNumber(document.querySelector('input[name="volume_second"]')?.value)
    const secondMarketValue = this.parseFormattedNumber(document.querySelector('input[name="market_value_second"]')?.value)

    // Calculate differences
    const marketValueDifference = secondMarketValue - firstMarketValue
    const yieldDifference = secondYield - firstYield
    const creditSpreadDifference = secondMargin - firstMargin

    // Update the table
    const marketValueElement = document.getElementById('market-value-difference')
    const yieldElement = document.getElementById('yield-difference')
    const creditSpreadElement = document.getElementById('credit-spread-difference')

    if (marketValueElement) {
      marketValueElement.textContent = this.formatCurrency(marketValueDifference)
    }

    if (yieldElement) {
      yieldElement.textContent = this.formatPercentage(yieldDifference)
    }

    if (creditSpreadElement) {
      creditSpreadElement.textContent = this.formatPercentage(creditSpreadDifference)
    }
  }

  // Format currency values
  formatCurrency(value) {
    if (isNaN(value) || value === 0) return '-'
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  // Format percentage values
  formatPercentage(value) {
    if (isNaN(value) || value === 0) return '-'
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
  }

  // Sync volume fields between visible and hidden sections
  syncVolumeFields() {
    const visibleVolumeField = document.querySelector('[data-calculator-target="rightColumn"] input[name="volume_first"]')
    const hiddenVolumeField = document.querySelector('input[name="volume_first"]')

    if (visibleVolumeField && hiddenVolumeField) {
      // Sync both fields with the stored volume
      visibleVolumeField.value = this.storedVolumeFirst
      hiddenVolumeField.value = this.storedVolumeFirst
    }
  }

  // Method to be called after calculation results are loaded (for external use)
  onCalculationResultsLoaded() {
    this.hideAllValidationMessages()

    // Check if second calculator is active
    const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')

    // Always recalculate Market Value fields after server response
    setTimeout(() => {
      this.recalculateMarketValues('first')
      if (isSecondCalculatorActive) {
        this.recalculateMarketValues('second')
      }
    }, 500)

    if (isSecondCalculatorActive) {
      // Wait a bit for the DOM to be updated with new calculation results
      // But don't call updateFirstCalculatorRiskMarket as it overrides calculated values
      setTimeout(() => {
        this.updateComparisonTable()
      }, 300)
    } else {
      // Auto-generate chart for single calculator
      setTimeout(() => {
        this.generateChartData()
      }, 600)

      // Update comparison table even for single calculator (in case it becomes dual later)
      setTimeout(() => {
        this.updateComparisonTable()
      }, 300)
    }

    // Update cash flow sections visibility
    this.updateCashFlowSectionsVisibility()
  }

  // Parse a formatted number string (removes commas and converts to number)
  parseFormattedNumber(value) {
    if (typeof value === 'string') {
      // Remove commas and parse
      return parseFloat(value.replace(/,/g, '')) || 0
    }
    return parseFloat(value) || 0
  }

  // Format number with thousands separators
  formatNumberWithSeparators(value) {
    if (isNaN(value) || value === 0) return '0.00'

    // Check if the value is already formatted (contains commas)
    if (typeof value === 'string' && value.includes(',')) {
      return value
    }

    return new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  // Recalculate Market Value fields based on current pricing data
  recalculateMarketValues(calculatorType = 'first') {
    const prefix = calculatorType === 'second' ? 'Second' : 'First'
    const namePrefix = calculatorType === 'second' ? '_second' : '_first'

    // Get current pricing values
    const grossField = document.querySelector(`[data-calculator-target="gross${prefix}"]`)
    const accruedField = document.querySelector(`[data-calculator-target="accrued${prefix}"]`)

    if (!grossField || !accruedField) return

    const grossValue = this.parseFormattedNumber(grossField.value)
    const accruedValue = this.parseFormattedNumber(accruedField.value)

    // Use stored volume value instead of reading from DOM
    const storedVolume = calculatorType === 'second' ? this.storedVolumeSecond : this.storedVolumeFirst
    const volumeValue = this.parseFormattedNumber(storedVolume)

    // Only recalculate if we have valid values
    if (grossValue === 0 && accruedValue === 0 && volumeValue === 0) return

    // Convert percentage values to decimals (gross and accrued are stored as percentages)
    const grossDecimal = grossValue / 100
    const accruedDecimal = accruedValue / 100

    // Calculate face value (volume * par_value, defaulting to 100)
    const faceValue = volumeValue * 100 // Default par_value is 100

    // Calculate market values using legacy calculator logic
    const adjustedParValue = 100 // Default adjusted_par_value
    const marketValue = (faceValue * grossDecimal) / adjustedParValue
    const accruedInterest = (accruedDecimal * faceValue) / adjustedParValue
    const capitalValue = marketValue - accruedInterest

    // For first calculator, always update the currently visible section
    if (calculatorType === 'first') {
      const isSecondCalculatorActive = !this.secondCalculatorTarget.classList.contains('hidden')

      if (isSecondCalculatorActive) {
        // Update hidden section (which becomes visible when second calculator is active)
        const marketCapitalValueField = document.querySelector('input[name="market_capital_value_first_hidden"]')
        const marketAccruedInterestField = document.querySelector('input[name="market_accrued_interest_first_hidden"]')
        const marketValueField = document.querySelector('input[name="market_value_first_hidden"]')

        if (marketCapitalValueField) marketCapitalValueField.value = this.formatNumberWithSeparators(capitalValue)
        if (marketAccruedInterestField) marketAccruedInterestField.value = this.formatNumberWithSeparators(accruedInterest)
        if (marketValueField) marketValueField.value = this.formatNumberWithSeparators(marketValue)
      } else {
        // Update visible section (when only first calculator is active)
        const marketCapitalValueField = document.querySelector('input[name="market_capital_value_first"]')
        const marketAccruedInterestField = document.querySelector('input[name="market_accrued_interest_first"]')
        const marketValueField = document.querySelector('input[name="market_value_first"]')

        if (marketCapitalValueField) marketCapitalValueField.value = this.formatNumberWithSeparators(capitalValue)
        if (marketAccruedInterestField) marketAccruedInterestField.value = this.formatNumberWithSeparators(accruedInterest)
        if (marketValueField) marketValueField.value = this.formatNumberWithSeparators(marketValue)
      }
    } else {
      // For second calculator, use standard field names
      const marketCapitalValueField = document.querySelector(`input[name="market_capital_value${namePrefix}"]`)
      const marketAccruedInterestField = document.querySelector(`input[name="market_accrued_interest${namePrefix}"]`)
      const marketValueField = document.querySelector(`input[name="market_value${namePrefix}"]`)

      if (marketCapitalValueField) marketCapitalValueField.value = this.formatNumberWithSeparators(capitalValue)
      if (marketAccruedInterestField) marketAccruedInterestField.value = this.formatNumberWithSeparators(accruedInterest)
      if (marketValueField) marketValueField.value = this.formatNumberWithSeparators(marketValue)
    }
  }



  // Set all current field values
  setUserInputFields(form) {
    // Determine which calculator this form belongs to
    const calculationField = form.querySelector('input[name="calculation"]')
    const isSecondCalculator = calculationField && calculationField.value === 'second'

    if (isSecondCalculator) {
      // Set fields for second calculator
      const yieldField = document.querySelector('[data-calculator-target="yieldSecond"]')
      if (yieldField) {
        const formYieldField = form.querySelector('input[name="yield"]')
        if (formYieldField) {
          formYieldField.value = yieldField.value || ''
        }
      }

      const marginField = document.querySelector('[data-calculator-target="marginSecond"]')
      if (marginField) {
        const formMarginField = form.querySelector('input[name="margin"]')
        if (formMarginField) {
          formMarginField.value = marginField.value || ''
        }
      }

      const capitalPriceField = document.querySelector('[data-calculator-target="capitalPriceSecond"]')
      if (capitalPriceField) {
        const formCapitalPriceField = form.querySelector('input[name="capital_price"]')
        if (formCapitalPriceField) {
          formCapitalPriceField.value = capitalPriceField.value || ''
        }
      }

      const grossField = document.querySelector('[data-calculator-target="grossSecond"]')
      if (grossField) {
        const formGrossField = form.querySelector('input[name="gross"]')
        if (formGrossField) {
          formGrossField.value = grossField.value || ''
        }
      }

      // Set date_as_at for second calculator
      const dateField = document.querySelector('[data-calculator-target="dateAsAtSecond"]')
      if (dateField && dateField.value) {
        const formDateField = form.querySelector('input[name="date_as_at"]')
        if (formDateField) {
          formDateField.value = dateField.value
        }
      }
    } else {
      // Set fields for first calculator
      const yieldField = document.querySelector('[data-calculator-target="yieldFirst"]')
      if (yieldField) {
        const formYieldField = form.querySelector('input[name="yield"]')
        if (formYieldField) {
          formYieldField.value = yieldField.value || ''
        }
      }

      const marginField = document.querySelector('[data-calculator-target="marginFirst"]')
      if (marginField) {
        const formMarginField = form.querySelector('input[name="margin"]')
        if (formMarginField) {
          formMarginField.value = marginField.value || ''
        }
      }

      const capitalPriceField = document.querySelector('[data-calculator-target="capitalPriceFirst"]')
      if (capitalPriceField) {
        const formCapitalPriceField = form.querySelector('input[name="capital_price"]')
        if (formCapitalPriceField) {
          formCapitalPriceField.value = capitalPriceField.value || ''
        }
      }

      const grossField = document.querySelector('[data-calculator-target="grossFirst"]')
      if (grossField) {
        const formGrossField = form.querySelector('input[name="gross"]')
        if (formGrossField) {
          formGrossField.value = grossField.value || ''
        }
      }

      // Set date_as_at for first calculator
      const dateField = document.querySelector('[data-calculator-target="dateAsAtFirst"]')
      if (dateField && dateField.value) {
        const formDateField = form.querySelector('input[name="date_as_at"]')
        if (formDateField) {
          formDateField.value = dateField.value
        }
      }
    }
  }

  // Setup Turbo Stream listener to detect when calculation results are loaded
  setupTurboStreamListener() {
    // Listen for Turbo Stream events
    document.addEventListener('turbo:before-stream-render', (event) => {
      const stream = event.detail.newStream
      const targetId = stream.getAttribute('target')
      // Check if this is a calculator result update
      if (targetId === 'calculator-content' || targetId === 'right-column' || targetId === 'risk-section' || targetId === 'market-value-section') {
        // Schedule update after the stream is rendered
        setTimeout(() => {
          this.onCalculationResultsLoaded()
        }, 100)
      }
    })

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.target.id === 'calculator-content') {
          setTimeout(() => {
            this.onCalculationResultsLoaded()
          }, 100)
        }
      })
    })

    const calculatorContent = document.getElementById('calculator-content')
    if (calculatorContent) {
      observer.observe(calculatorContent, { childList: true, subtree: true })
    }

    const rightColumnObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Check if any risk or market value elements were updated
          const riskElements = ['term-to-maturity', 'modified-duration', 'credit-duration']
          const marketElements = ['volume', 'currency', 'market_capital_price', 'market_accrued_interest', 'market_gross_price']

          let shouldUpdate = false

          // Check if any risk elements were updated
          riskElements.forEach(id => {
            const element = document.getElementById(id)
            if (element && mutation.target.contains(element)) {
              shouldUpdate = true
            }
          })

          // Check if any market value elements were updated
          marketElements.forEach(name => {
            const element = document.querySelector(`input[name="${name}"]`)
            if (element && mutation.target.contains(element)) {
              shouldUpdate = true
            }
          })

          if (shouldUpdate) {
            setTimeout(() => {
              this.onCalculationResultsLoaded()
            }, 100)
          }
        }
      })
    })

    const rightColumn = document.querySelector('[data-calculator-target="rightColumn"]')
    if (rightColumn) {
      rightColumnObserver.observe(rightColumn, { childList: true, subtree: true, characterData: true })
    }
  }
}
